import * as T from 'three';
import type { Traversal } from './types';

/**
 * Traversal visual grammar (Design Bible §6). The physics contact for every branch stays the
 * authored straight segment a→b; visuals may arc around it but only by a fraction of the collider
 * radius, so a hand placed on the solver's anchor always lands on visible bark.
 */
export type BranchStyle = 'strong' | 'standard' | 'flexible' | 'dead';

export function branchStyle(o: Traversal): BranchStyle {
    if (o.type === 7 || o.tags.includes('dead') || o.tags.includes('breakable')) return 'dead';
    if (o.radius >= .4 || o.tags.includes('safe') || o.tags.includes('recovery')) return 'strong';
    if (o.tags.includes('expert') || o.tags.includes('flexible')) return 'flexible';
    return 'standard';
}

/** Maximum visual departure from the collision line, in metres. */
export function branchSag(o: Traversal) { return o.type === 1 || o.type === 7 ? Math.min(.1, o.radius * .4) : 0; }

/** A point on the grabbable span (t ∈ [0, 1]) with its gentle upward arc. */
export function spanPoint(o: Traversal, t: number, target = new T.Vector3()) {
    target.set(
        o.a[0] + (o.b[0] - o.a[0]) * t,
        o.a[1] + (o.b[1] - o.a[1]) * t,
        o.a[2] + (o.b[2] - o.a[2]) * t,
    );
    target.y += branchSag(o) * Math.sin(Math.PI * Math.min(1, Math.max(0, t)));
    return target;
}

/** The curve the highlight shell and the bark tube share for the catchable part of a branch. */
export function spanCurve(o: Traversal, samples = 8) {
    const points: T.Vector3[] = [];
    for (let i = 0; i <= samples; i++) points.push(spanPoint(o, i / samples));
    return new T.CatmullRomCurve3(points);
}

/** Visual radius multipliers at the base and tip of the grabbable span. */
export function branchTaper(style: BranchStyle): [number, number] {
    return style === 'strong' ? [1.18, .9] : style === 'flexible' ? [.92, .58] : style === 'dead' ? [.96, .72] : [1.05, .74];
}

/** Tube with a per-ring radius profile; rings follow the curve's arc-length parameter. */
export function taperedTube(curve: T.Curve<T.Vector3>, segments: number, radial: number, radius: (t: number) => number) {
    const g = new T.TubeGeometry(curve, segments, 1, radial, false);
    const pos = g.getAttribute('position');
    const center = new T.Vector3(), point = new T.Vector3();
    for (let i = 0; i <= segments; i++) {
        const t = i / segments, r = radius(t);
        curve.getPointAt(t, center);
        for (let j = 0; j <= radial; j++) {
            const ix = i * (radial + 1) + j;
            point.fromBufferAttribute(pos, ix).sub(center).multiplyScalar(r).add(center);
            pos.setXYZ(ix, point.x, point.y, point.z);
        }
    }
    // TubeGeometry's own normals stay valid for a gentle taper and avoid seam artefacts.
    return g;
}
